import {
  type FC,
  useDeferredValue,
  useMemo,
  useState,
  useTransition,
} from "react";

import type { DashboardAdminAgentModeBudgetDefault } from "../../dashboard-client";

import { useAdmin } from "../../hooks/useAdmin";
import { useAdminAgentModeBudgets } from "../../hooks/useAdminAgentModeBudgets";
import { useFormAction } from "../../hooks/useFormAction";
import { Alert } from "../common/Alert";
import { Badge } from "../common/Badge";
import { Button } from "../common/Button";
import { DataTable } from "../common/DataTable";
import { EmptyState } from "../common/EmptyState";
import { FormCheckbox } from "../common/forms/FormCheckbox";
import { FormInput } from "../common/forms/FormInput";
import { FormSelect } from "../common/forms/FormSelect";
import { LoadingState } from "../common/LoadingState";
import { MetricCard } from "../common/MetricCard";
import { PanelSection } from "../common/PanelSection";

const ALL_MODES_VALUE = "";

function formatBudget(value: number | null): string {
  return value == null ? "Unbudgeted" : value.toLocaleString();
}

function groupDefaults(defaults: DashboardAdminAgentModeBudgetDefault[]) {
  const grouped = new Map<string, DashboardAdminAgentModeBudgetDefault[]>();
  for (const entry of defaults) {
    const list = grouped.get(entry.mode) ?? [];
    list.push(entry);
    grouped.set(entry.mode, list);
  }
  return Array.from(grouped.entries())
    .toSorted(([a], [b]) => a.localeCompare(b))
    .map(([mode, phases]) => ({
      mode,
      phases: phases.toSorted((a, b) => a.phase.localeCompare(b.phase)),
    }));
}

function formatSummaryLabel(
  label: string,
  value: string | number,
  tone: "default" | "emerald" | "indigo" | "violet",
) {
  const toneClasses = {
    default:
      "border-slate-300 bg-slate-50/80 dark:border-slate-700 dark:bg-slate-900/50",
    emerald:
      "border-emerald-200 bg-emerald-50/80 dark:border-emerald-500/30 dark:bg-emerald-500/10",
    indigo:
      "border-indigo-200 bg-indigo-50/80 dark:border-indigo-500/30 dark:bg-indigo-500/10",
    violet:
      "border-violet-200 bg-violet-50/80 dark:border-violet-500/30 dark:bg-violet-500/10",
  };

  return (
    <div className={`border p-3 ${toneClasses[tone]}`}>
      <p className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
        {label}
      </p>
      <p className="mt-1 font-mono text-sm font-bold text-slate-900 dark:text-white">
        {value}
      </p>
    </div>
  );
}

const OrganizationSelectionRow: FC<{
  isSelected: boolean;
  onToggle: () => void;
  organization: {
    name: string;
    organization_id: string;
    plan_tier: string;
    slug: string;
  };
}> = ({ isSelected, onToggle, organization }) => (
  <label className="flex cursor-pointer items-start justify-between gap-3 border border-slate-200 bg-white px-3 py-2 transition-colors hover:border-emerald-400 hover:bg-emerald-50/60 dark:border-slate-700 dark:bg-arti-background dark:hover:border-emerald-500 dark:hover:bg-emerald-500/10">
    <div className="flex items-start gap-3">
      <input
        type="checkbox"
        checked={isSelected}
        onChange={onToggle}
        className="mt-0.5 h-4 w-4 rounded-none border border-slate-300 text-emerald-500 focus:ring-emerald-500 dark:border-slate-600 dark:bg-arti-background"
      />
      <div>
        <p className="font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-slate-900 dark:text-white">
          {organization.name}
        </p>
        <p className="mt-1 font-mono text-[10px] text-slate-500 dark:text-slate-400">
          {organization.slug}
        </p>
      </div>
    </div>
    <Badge size="sm" variant="status">
      {organization.plan_tier}
    </Badge>
  </label>
);

const DefaultBudgetRow: FC<{
  index: number;
  mode: string;
  phase: DashboardAdminAgentModeBudgetDefault;
}> = ({ index, mode, phase }) => {
  const RowEl = index === 0 ? DataTable.Row : DataTable.SubRow;

  return (
    <RowEl>
      <DataTable.Cell>
        {index === 0 ? (
          <Badge variant="info" size="sm">
            {mode}
          </Badge>
        ) : null}
      </DataTable.Cell>
      <DataTable.Cell>
        <span className="font-mono text-[11px] font-bold uppercase tracking-[0.16em] text-slate-900 dark:text-white">
          {phase.phase}
        </span>
      </DataTable.Cell>
      <DataTable.Cell className="text-right font-mono text-xs text-slate-600 dark:text-slate-300">
        {formatBudget(phase.max_input_token_budget)}
      </DataTable.Cell>
      <DataTable.Cell>
        <Badge
          variant={phase.max_input_token_budget == null ? "warning" : "success"}
          size="sm"
        >
          {phase.max_input_token_budget == null ? "Unbudgeted" : "Budgeted"}
        </Badge>
      </DataTable.Cell>
    </RowEl>
  );
};

export const AdminAgentModeBudgetsTab: FC = () => {
  const {
    organizations,
    loading: organizationsLoading,
    error: organizationsError,
  } = useAdmin();
  const {
    defaults,
    error: budgetsError,
    lastResult,
    loading: budgetsLoading,
    reseed,
  } = useAdminAgentModeBudgets();
  const { error: actionError, message, run, saving } = useFormAction();

  const [selectedMode, setSelectedMode] = useState(ALL_MODES_VALUE);
  const [organizationQuery, setOrganizationQuery] = useState("");
  const [targetAllOrganizations, setTargetAllOrganizations] = useState(true);
  const [selectedOrganizationIds, setSelectedOrganizationIds] = useState<
    string[]
  >([]);
  const [isSelecting, startTransition] = useTransition();

  const deferredOrganizationQuery = useDeferredValue(organizationQuery);
  const groupedDefaults = useMemo(() => groupDefaults(defaults), [defaults]);
  const modeOptions = useMemo(
    () => [
      { label: "All Modes", value: ALL_MODES_VALUE },
      ...groupedDefaults.map(({ mode }) => ({ label: mode, value: mode })),
    ],
    [groupedDefaults],
  );

  const visibleOrganizations = useMemo(() => {
    const normalized = deferredOrganizationQuery.trim().toLowerCase();
    return organizations.filter((organization) => {
      if (!normalized) {
        return true;
      }
      return (
        organization.name.toLowerCase().includes(normalized) ||
        organization.slug.toLowerCase().includes(normalized)
      );
    });
  }, [deferredOrganizationQuery, organizations]);

  const targetedOrganizationIds = useMemo(() => {
    if (targetAllOrganizations) {
      return organizations.map((organization) => organization.organization_id);
    }
    const existingIds = new Set(
      organizations.map((organization) => organization.organization_id),
    );
    return selectedOrganizationIds.filter((organizationId) =>
      existingIds.has(organizationId),
    );
  }, [organizations, selectedOrganizationIds, targetAllOrganizations]);

  const targetedDefaultGroups = useMemo(() => {
    if (!selectedMode) {
      return groupedDefaults;
    }
    return groupedDefaults.filter(({ mode }) => mode === selectedMode);
  }, [groupedDefaults, selectedMode]);

  const selectedPhaseCount = targetedDefaultGroups.reduce(
    (total, group) => total + group.phases.length,
    0,
  );
  const estimatedRows = selectedPhaseCount * targetedOrganizationIds.length;

  const hasSelectionError =
    !targetAllOrganizations && targetedOrganizationIds.length === 0;
  const isLoading = organizationsLoading || budgetsLoading;
  const loadError = budgetsError ?? organizationsError;

  function toggleOrganization(organizationId: string) {
    setSelectedOrganizationIds((current) =>
      current.includes(organizationId)
        ? current.filter((candidate) => candidate !== organizationId)
        : [...current, organizationId].toSorted(),
    );
  }

  function selectVisibleOrganizations() {
    startTransition(() => {
      setSelectedOrganizationIds(
        visibleOrganizations
          .map((organization) => organization.organization_id)
          .toSorted(),
      );
    });
  }

  function clearSelectedOrganizations() {
    startTransition(() => {
      setSelectedOrganizationIds([]);
    });
  }

  async function handleReseed() {
    if (hasSelectionError) {
      return;
    }

    await run(async () => {
      const result = await reseed({
        mode: selectedMode || undefined,
        organizationIds: targetAllOrganizations
          ? undefined
          : targetedOrganizationIds,
      });
      const organizationLabel =
        result.targeted_organization_count === 1
          ? "organization"
          : "organizations";
      return `Inserted ${result.inserted} and updated ${result.updated} rows across ${result.targeted_organization_count} ${organizationLabel}.`;
    });
  }

  if (isLoading) {
    return <LoadingState message="Loading budget control center…" />;
  }
  if (loadError) {
    return <Alert variant="error">{loadError}</Alert>;
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <MetricCard
          label="Modes"
          value={groupedDefaults.length}
          variant="indigo"
          size="lg"
        />
        <MetricCard
          label="Default Phases"
          value={defaults.length}
          variant="violet"
          size="lg"
        />
        <MetricCard
          label="Targeted Orgs"
          value={targetedOrganizationIds.length}
          variant="emerald"
          size="lg"
        />
        <MetricCard
          label="Estimated Rows"
          value={estimatedRows}
          variant="amber"
          size="lg"
        />
      </div>

      {message ? (
        <Alert variant="success" title="Reseed Complete">
          {message}
        </Alert>
      ) : null}
      {actionError ? (
        <Alert variant="error" title="Reseed Failed">
          {actionError}
        </Alert>
      ) : null}
      {hasSelectionError ? (
        <Alert variant="warning" title="Organization Selection Required">
          Select at least one organization or switch back to targeting all
          organizations.
        </Alert>
      ) : null}

      <PanelSection
        title="Budget Control Center"
        badge={<Badge size="sm">{selectedMode || "all modes"}</Badge>}
      >
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.25fr)_minmax(320px,0.75fr)]">
          <div className="space-y-4">
            <div className="grid gap-3 md:grid-cols-2">
              <FormSelect
                label="Mode Target"
                value={selectedMode}
                onChange={(event) => setSelectedMode(event.currentTarget.value)}
                options={modeOptions}
              />
              <FormInput
                label="Organization Filter"
                value={organizationQuery}
                onChange={(event) =>
                  setOrganizationQuery(event.currentTarget.value)
                }
                placeholder="Search by name or slug"
                helperText={`${visibleOrganizations.length} organizations match the current filter.`}
              />
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <FormCheckbox
                label="Target All Organizations"
                description="Writes the current defaults for every organization."
                checked={targetAllOrganizations}
                onChange={setTargetAllOrganizations}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={selectVisibleOrganizations}
                disabled={
                  targetAllOrganizations || visibleOrganizations.length === 0
                }
              >
                {isSelecting ? "Selecting..." : "Select Visible"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearSelectedOrganizations}
                disabled={
                  targetAllOrganizations || selectedOrganizationIds.length === 0
                }
              >
                Clear Selection
              </Button>
            </div>

            {!targetAllOrganizations ? (
              <div className="max-h-80 space-y-2 overflow-auto border border-slate-300 bg-slate-50/70 p-3 dark:border-slate-700 dark:bg-slate-900/40">
                {visibleOrganizations.length === 0 ? (
                  <EmptyState
                    title="No organizations match"
                    description="Try a different filter or target all organizations."
                    size="sm"
                  />
                ) : (
                  visibleOrganizations.map((organization) => (
                    <OrganizationSelectionRow
                      key={organization.organization_id}
                      isSelected={selectedOrganizationIds.includes(
                        organization.organization_id,
                      )}
                      onToggle={() =>
                        toggleOrganization(organization.organization_id)
                      }
                      organization={organization}
                    />
                  ))
                )}
              </div>
            ) : null}
          </div>

          <div className="space-y-3">
            <div className="border border-slate-300 bg-gradient-to-br from-indigo-50 via-white to-emerald-50 p-4 dark:border-slate-700 dark:from-indigo-500/10 dark:via-arti-panel dark:to-emerald-500/10">
              <p className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                Operation Preview
              </p>
              <div className="mt-3 grid gap-2">
                {formatSummaryLabel(
                  "Mode Scope",
                  selectedMode || "All defaulted modes",
                  "indigo",
                )}
                {formatSummaryLabel("Phases", selectedPhaseCount, "violet")}
                {formatSummaryLabel(
                  "Organization Scope",
                  targetAllOrganizations
                    ? `All organizations (${organizations.length})`
                    : `${targetedOrganizationIds.length} selected`,
                  "emerald",
                )}
                {formatSummaryLabel(
                  "Expected Upserts",
                  estimatedRows,
                  "default",
                )}
              </div>
            </div>

            <div className="border border-slate-300 bg-slate-50/70 p-4 dark:border-slate-700 dark:bg-slate-900/40">
              <p className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                Behavior
              </p>
              <p className="mt-2 text-xs leading-5 text-slate-600 dark:text-slate-300">
                Reseeding overwrites each targeted organization’s persisted
                mode-phase rows with the current platform defaults. Use it when
                you want existing organizations to pick up new default budgets.
              </p>
            </div>

            <Button
              fullWidth
              variant="primary"
              size="md"
              onClick={() => void handleReseed()}
              loading={saving}
              loadingText="Reseeding..."
              disabled={hasSelectionError || selectedPhaseCount === 0}
            >
              Reseed Budgets
            </Button>

            {lastResult ? (
              <div className="border border-emerald-200 bg-emerald-50/70 p-3 dark:border-emerald-500/30 dark:bg-emerald-500/10">
                <p className="font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">
                  Last Result
                </p>
                <p className="mt-1 font-mono text-xs text-emerald-800 dark:text-emerald-100">
                  {lastResult.inserted} inserted, {lastResult.updated} updated
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </PanelSection>

      <PanelSection
        title="Current Mode Defaults"
        badge={<Badge size="sm">{defaults.length}</Badge>}
      >
        {targetedDefaultGroups.length === 0 ? (
          <EmptyState
            title="No defaults available"
            description="The selected mode does not expose any default phase budgets."
            size="sm"
          />
        ) : (
          <DataTable responsive>
            <DataTable.Header>
              <DataTable.Row>
                <DataTable.HeaderCell>Mode</DataTable.HeaderCell>
                <DataTable.HeaderCell>Phase</DataTable.HeaderCell>
                <DataTable.HeaderCell className="text-right">
                  Default Budget
                </DataTable.HeaderCell>
                <DataTable.HeaderCell>Status</DataTable.HeaderCell>
              </DataTable.Row>
            </DataTable.Header>
            {targetedDefaultGroups.map(({ mode, phases }) => (
              <DataTable.RowGroup key={mode}>
                {phases.map((phase, index) => (
                  <DefaultBudgetRow
                    key={`${mode}::${phase.phase}`}
                    index={index}
                    mode={mode}
                    phase={phase}
                  />
                ))}
              </DataTable.RowGroup>
            ))}
          </DataTable>
        )}
      </PanelSection>
    </div>
  );
};
